import { Colors } from '@vetahealth/fishing-gear/colors'
import {
  convert,
  defaultUnits,
  getDisplayValue,
  normalize,
  normalizedUnitRanges,
  normalizedUnits,
} from '@vetahealth/fishing-gear/conversions'
import { CardDetails } from '@vetahealth/tuna-can-api'
import i18next from 'i18next'

import { LineChart } from '../Chart/LineChart'
import { createAlertField, createDeviceField } from '../Table/helpers'
import { GetResultsConfigParams, ResultsConfig } from './types'

export function getRespiratoryRateConfig({ userUnits }: GetResultsConfigParams): ResultsConfig {
  const unit = userUnits.respiratoryRate || defaultUnits.respiratoryRate

  const [min, max] = normalizedUnitRanges.respiratoryRate
  const [convertedMin] = convert(min, normalizedUnits.respiratoryRate, 'respiratoryRate', unit)
  const [convertedMax] = convert(max, normalizedUnits.respiratoryRate, 'respiratoryRate', unit)

  return {
    key: 'respiratoryRate',
    title: i18next.t('tracking.respiratoryRate.title'),
    category: 'tracking',
    unit,
    format: (event, targetUnit, omitUnit) =>
      event.value !== undefined
        ? getDisplayValue(
            [event.value, event.additionalValue ?? 0],
            event.unit ?? normalizedUnits.respiratoryRate,
            'respiratoryRate',
            { targetUnit, omitUnit },
          )
        : '–',
    actions: {
      add: [
        {
          key: 'value',
          label: i18next.t('tracking.respiratoryRate.respiratoryRate', { unit }),
          validators: [
            { required: true, message: i18next.t('validations.enterRespiratoryRate') },
            {
              min,
              max,
              type: 'number',
              transform: (value) => normalize('respiratoryRate', Number(value), unit)[0],
              message: i18next.t('validations.trackingRange', {
                min: convertedMin,
                max: convertedMax,
                unit,
              }),
            },
          ],
        },
      ],
      reviewBulk: true,
      downloadCsv: true,
    },
    chart: {
      component: LineChart,
      intervals: ['day', 'week', 'month', 'trend'],
      dataKeys: [{ key: 'value', color: Colors.azure500 }],
      getData: (data: CardDetails[]) =>
        data.map((event) => ({
          ...event,
          value: convert(event.value, event.unit, 'respiratoryRate', unit)[0],
        })),
    },
    table: {
      defaultFields: true,
      additionalFields: [createAlertField(), createDeviceField()],
      actions: ['review', 'delete'],
    },
  }
}
